import { useMemo, useState } from "react";
import { Box, Heading, Tooltip, Icon, HStack, Portal, Text } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import BranchServices from "../../components/branches/reports/BranchServices";
import { ARRIVAL_RATES_FREQUENCIES, REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import FilterForm from "../../components/FilterForm";
import Loader from "../../components/loader";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import useReportRenderSettingLoader from "../../hooks/useReportRenderSettingLoader";
import {
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  DEFAULT_RENDER_CONFIG,
  getRenderConfigSummaryItems,
  normalizeRenderConfig,
} from "../../store/renderConfig";
import { saveReportRenderSetting } from "../../store/actions/defaultAction";
import { downloadArrivalRatesPerServiceCsvFiles } from "../../store/arrivalRatesPerServiceExport";

const ArrivalRatesPerService = ({ showFilterForm = true, showTitle = 1 }) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.authReducer);
  const {
    loading,
    reportRange,
    reportsData,
    branchId,
    organizationId,
    savingReportRenderSetting,
  } = useSelector((state) => state.defaultReducer);
  const [renderConfig, setRenderConfig] = useState(DEFAULT_RENDER_CONFIG);
  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_service
      : REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_service;
  const data = useMemo(() => {
    let reports = reportsData.filter((item) => item.reportTypeId === reportTypeId);
    if (branchId.length > 0) reports = reports.filter((item) => item.branchId === branchId);
    return reports.map((item) => item.data) || [];
  }, [branchId, reportTypeId, reportsData]);
  const renderConfigOptions = useMemo(
    () => ({ columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS }),
    []
  );
  const isLoadingRenderSetting = useReportRenderSettingLoader({
    userId: userData?.id,
    organizationId,
    reportTypeId,
    renderConfigOptions,
    setRenderConfig,
  });
  const effectiveRenderConfig = useMemo(
    () => normalizeRenderConfig(renderConfig, renderConfigOptions),
    [renderConfig, renderConfigOptions]
  );
  const presentationSummaryItems = useMemo(
    () => getRenderConfigSummaryItems(effectiveRenderConfig, renderConfigOptions),
    [effectiveRenderConfig, renderConfigOptions]
  );
  const csvScopes = useMemo(
    () =>
      data.map((item) => ({
        scope: "Branch",
        branchName: item.branchName,
        branchTotalArrivals: item.branchTotalArrivals,
        data: item.servicesData,
      })),
    [data]
  );

  const saveRenderConfig = () => {
    dispatch(
      saveReportRenderSetting({
        userId: userData.id,
        organizationId,
        reportTypeId,
        renderConfig: effectiveRenderConfig,
      })
    );
  };

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} Branch Arrival Rates Per Service</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {loading || isLoadingRenderSetting ? <Loader /> : (
        <>
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDirection="row" justifyContent="space-between">
                <Heading fontSize="34px" color="#222" fontWeight="700">
                  Arrival Rates Per Service
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span><Icon as={Info} w={5} h={5} ml={2} cursor="pointer" /></span>
                    </Tooltip.Trigger>
                    <Portal>
                      <Tooltip.Positioner>
                        <Tooltip.Content>
                          {getReportDescription(reportRange).BranchesArrivalRatesPerService}
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Portal>
                  </Tooltip.Root>
                </Heading>
                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={effectiveRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={savingReportRenderSetting}
                  onDownloadCsv={() =>
                    downloadArrivalRatesPerServiceCsvFiles({
                      scopes: csvScopes,
                      renderConfig: effectiveRenderConfig,
                      filenamePrefix: "branch_arrival_rates_per_service",
                    })
                  }
                />
              </HStack>
              <ReportPresentationControls
                renderConfig={effectiveRenderConfig}
                onChange={setRenderConfig}
                columnDefinitions={ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS}
                defaultSortColumn="total"
              />
              <ReportPresentationSummary items={presentationSummaryItems} />
              <BranchServices
                frequency={ARRIVAL_RATES_FREQUENCIES.DAY}
                data={data}
                renderConfig={effectiveRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
            <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
          )}
        </>
      )}
    </Box>
  );
};

export default ArrivalRatesPerService;
